/**
 * Typed cache policy tiers for TanStack Query.
 *
 * Use these as spread defaults in queryOptions() factories to express intent:
 *
 *   queryOptions({ ...queryPolicyDefaults.warm, queryKey: [...], queryFn: ... })
 */

export interface QueryPolicy {
  /** How long (ms) data is considered fresh. 0 = always stale. */
  staleTime: number;
  /** How long (ms) unused data is kept in the cache after all observers unmount. */
  gcTime: number;
}

export const queryPolicyDefaults = {
  /**
   * ephemeral — for real-time/frequently changing data.
   * Always refetches on mount. Cache is discarded after 30s of inactivity.
   */
  ephemeral: {
    staleTime: 0,
    gcTime: 30_000,
  },

  /**
   * warm — for moderately dynamic data (default for most feature queries).
   * Fresh for 30s, retained in cache for 5 minutes.
   */
  warm: {
    staleTime: 30_000,
    gcTime: 5 * 60 * 1000,
  },

  /**
   * persistent — for mostly-static reference data (lists, configs, enums).
   * Fresh for 10 minutes, retained for 24 hours.
   */
  persistent: {
    staleTime: 10 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
  },

  /**
   * realtime — for data driven by websockets/SSE.
   * Never auto-refetches (external push drives updates).
   * Cache discarded after 10s of inactivity.
   */
  realtime: {
    staleTime: Infinity,
    gcTime: 10_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false as const,
  },
} satisfies Record<string, QueryPolicy & Record<string, unknown>>;
